import React, { useContext, useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { createSelector } from 'reselect';

import OptionSettings from './OptionSettings';
import ButtonGroupSettings from './ButtonGroupSettings';
import InputSettingsField from './InputSettingsField';
import FieldStyleSettings from './FieldStyleSettings';
import PositionSettings from './PositionSettings';
import MetadataSettings from './MetadataSettings';
import IdentifierSelectorSettings from './IdentifierSelectorSettings';
import FieldSettingsHeaderTitle from './FieldSettingsHeaderTitle';
import Button from '../../misc/Button';
import Icon from '../../misc/Icon';
import SettingsTabs from './SettingsTabs';
import AccordionSettings from './AccordionSettings';
import SettingsLabel from './SettingsLabel';
import DateFormatSettings from './DateFormatSettings';
import { StyledFieldWrapper, StyledAccordionFieldWrapper, StyledButtonGroupWrapper, StyledButtonsWrapper, DividerLine } from './SettingsCommonElements';

import FieldDisplayTypes from '../../../constants/FieldDisplayTypes';
import FieldTableColumnTypes from '../../../constants/FieldTableColumnTypes';
import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

import { isSettingHidden, isStyleSettingHidden, getMetadataOptions } from '../../../lib/settingsHelper';
import { validateReadableId } from '@builttocreate/engine-utils';

const StyledWrapper = styled.div`
  padding: 12px 0px;
  display: flex;
  flex-direction: column;
`;

const StyledErrorMessage = styled.div`
  color: ${(props) => props.theme.dangerColor};
  font-size: 12px;
  margin-top: 4px;
  margin-left: 3px;
`;

const FieldSettingsTableCell = ({

  fieldId,
  fieldIdentifier,
  fieldPositionId,
  formula,

  activeFieldPosition,

  identifier, //Field identifier
  columnIdentifier, //Column identifier

  title,
  type,
  displayType,
  options,
  targetValue,
  metadata,
  maxImageWidth,
  maxImageHeight,

  config, //Field config
  columnConfig, //Column config

  columnOperand,
  rows,

  rowIndex,

  /**
   * Target column._id. An individual table cell fieldPosition stores
   * the associated target column._id under the "column" property.
   */
  column,
  columnType,
  multi,

  //Position
  x,
  y,
  width,
  height,

  //Field style
  fontSize,
  fontColor,
  fontStyle,
  fontWeight,
  textAlign,
  textTransform,
  textDecoration,
  textOverflow,
  borderColor,
  borderRadius,
  borderWidth,
  backgroundColor,
  padding,
  margin,
  zIndex,

  //Action handlers
  onChange,
  onDelete,
  onCapture,
  onColumnChange,
  onOptionCreate,
  onOptionChange,
  onOptionDelete,
  onPositionDuplicate,
  onPositionChange,

  disableDragAndDrop,
  fields,
  useReadableIds

}) => {

  const theme = useContext(ThemeContext);
  const [readableIdError, setReadableIdError] = useState('');

  const prevProps = useRef();

  useEffect(() => {

    if (prevProps.current && prevProps.current !== column && readableIdError) {
      setReadableIdError('');
    }

    prevProps.current = column

  }, [column, setReadableIdError]);

  const isStylesHidden = isSettingHidden(config, 'styles');
  const isDisplayTypeHidden = isSettingHidden(config, 'displayType');
  const isTitleHidden = isSettingHidden(config, 'title');
  const isValueHidden = isSettingHidden(config, 'value');
  const isOptionsHidden = isSettingHidden(config, 'options');
  const isIdentifierHidden = isSettingHidden(config, 'identifier');
  const isDeleteHidden = isSettingHidden(config, 'delete');
  const isIdHidden = isSettingHidden(config, '_id');

  //Text Styles
  const isFontSizeHidden = isStyleSettingHidden(config, 'fontSize');
  const isFontColorHidden = isStyleSettingHidden(config, 'fontColor');
  const isTextAlignHidden = isStyleSettingHidden(config, 'textAlign');
  const isFontStyleHidden = isStyleSettingHidden(config, 'fontStyle');
  const isFontWeightHidden = isStyleSettingHidden(config, 'fontWeight');
  const isTextTransformHidden = isStyleSettingHidden(config, 'textTransform');
  const isTextDecorationHidden = isStyleSettingHidden(config, 'textDecoration');
  const isTextOverflowHidden = isStyleSettingHidden(config, 'textOverflow');

  //Field Styles
  const isBorderColorHidden = isStyleSettingHidden(config, 'borderColor');
  const isBorderRadiusHidden = isStyleSettingHidden(config, 'borderRadius');
  const isBorderWidthHidden = isStyleSettingHidden(config, 'borderWidth');
  const isBackgroundColorHidden = isStyleSettingHidden(config, 'backgroundColor');
  const isMarginHidden = isStyleSettingHidden(config, 'margin');
  const isPaddingHidden = isStyleSettingHidden(config, 'padding');
  const isZIndexHidden = isStyleSettingHidden(config, 'zIndex');
  const isPositionHidden = isStyleSettingHidden(config, 'position');

  const isSelectorType = displayType && displayType !== FieldDisplayTypes.text;
  const isDropdownType = columnType === FieldTableColumnTypes.dropdown;
  const isMultiselectType = columnType === FieldTableColumnTypes.multiSelect;
  const isTextType = columnType === FieldTableColumnTypes.text;
  const isBarcodeType = columnType === FieldTableColumnTypes.barcode;
  const isImageType = columnType === FieldTableColumnTypes.image;
  const isNumberType = columnType === FieldTableColumnTypes.number;
  const isDateType = columnType === FieldTableColumnTypes.date;
  const isSignatureType = columnType === FieldTableColumnTypes.signature;

  const columnMetadataOptions = getMetadataOptions(config);

  const value = getCellValueSelector({ rowIndex, column, rows });
  const format = activeFieldPosition?.fieldPositionTableColumns?.[column]?.format;

  /**
   * Handler for directly updating the field position column
   */
  const handleFieldPositionColumnUpdate = (data) => {

    const existingFieldPositionTableColumns = activeFieldPosition?.fieldPositionTableColumns || {};

    const existingTargetColumn = existingFieldPositionTableColumns[column] || {};

    const nextFieldPositionTableColumns = {
      ...existingFieldPositionTableColumns,
      [column]: {
        ...existingTargetColumn,
        ...data,
      }
    };

    onPositionChange(fieldId, fieldIdentifier, fieldPositionId, { tableColumns: nextFieldPositionTableColumns });

  };

  const handleDefaultValueChange = (fieldId, value,) => {

    const nextRows = [...rows];

    const nextRow = { ...nextRows[rowIndex] };

    nextRow.cells = { ...nextRow.cells, [column]: value };

    nextRows[rowIndex] = nextRow;

    onChange(fieldId, fieldIdentifier, fieldPositionId, { value: nextRows });

  };

  if (isDropdownType && !options) return null;

  const validOptions = [];
  if (options) options.forEach((option) => { if (!option.deleted) validOptions.push(option); });

  let defaultValueNode = null;
  let formatNode = null;

  if (!isValueHidden && (isDropdownType || isMultiselectType) && !isSelectorType) {
    defaultValueNode = (
      <StyledFieldWrapper>
        <InputSettingsField
          type={isDropdownType ? 'dropdown' : 'multiselect'}
          allowMultiple={multi !== false}
          fieldId={fieldId}
          value={value}
          options={validOptions}
          onChange={(f, value) => handleDefaultValueChange(fieldId, value)}
        />
      </StyledFieldWrapper>
    );
  } else if (!isValueHidden && isTextType) {
    defaultValueNode = (
      <StyledFieldWrapper>
        <InputSettingsField
          fieldId={fieldId}
          value={value}
          type="textarea"
          onChange={(f, value) => handleDefaultValueChange(fieldId, value)}
        />
      </StyledFieldWrapper>
    );
  } else if (!isValueHidden && isBarcodeType) {
    defaultValueNode = (
      <StyledFieldWrapper>
        <InputSettingsField
          fieldId={fieldId}
          value={value}
          type="barcode"
          onChange={(f, value) => handleDefaultValueChange(fieldId, value)}
          onCapture={onCapture}
        />
      </StyledFieldWrapper>
    );
  }

  else if (!isValueHidden && isDateType) {

    defaultValueNode = null; 

    formatNode = (
      <>
        <DateFormatSettings
          format={format}
          customDateTimeFormatEnabled={config?.customDateTimeFormat}
          onChange={(value) => handleFieldPositionColumnUpdate({ format: value })}
        />
      </>
    );

  }

  else if (!isValueHidden && isNumberType) {
    defaultValueNode = (
      <StyledFieldWrapper>
        <InputSettingsField
          type="number"
          fieldId={fieldId}
          label="Default Value"
          description="Default new row cell value"
          value={value}
          options={validOptions}
          onChange={(i, value) => handleDefaultValueChange(fieldId, value)}
        />
      </StyledFieldWrapper>
    );
  }

  let imageSettingsNode = null;

  if (isImageType || isSignatureType) {

    const label = isImageType ? 'Image' : 'Signature';

    imageSettingsNode = (
      <>
        <StyledFieldWrapper>
          <InputSettingsField
            fieldId={fieldId}
            id={fieldId}
            label={`${label} Width`}
            value={maxImageWidth}
            type="number"
            onChange={(i, value) => onColumnChange(fieldId, fieldIdentifier, fieldPositionId, { maxImageWidth: value ? parseFloat(value, 10) : (isImageType ? 200 : 0) })}
          />
        </StyledFieldWrapper>
        <StyledFieldWrapper>
          <InputSettingsField
            fieldId={fieldId}
            id={fieldId}
            label={`${label} Height`}
            value={maxImageHeight}
            type="number"
            onChange={(i, value) => onColumnChange(fieldId, fieldIdentifier, fieldPositionId, { maxImageHeight: value ? parseFloat(value, 10) : (isImageType ? 200 : 0) })}
          />
        </StyledFieldWrapper>
      </>
    );
  }

  const stylesNode = (
    <>
      {isImageType || (
        isFontSizeHidden
        && isFontColorHidden
        && isTextAlignHidden
        && isFontStyleHidden
        && isFontWeightHidden
        && isTextTransformHidden
        && isTextDecorationHidden
        && isTextOverflowHidden
      ) ? null : (
        <>
          <AccordionSettings
            isOpen
            title="Text"
            height={170}
          >
            <FieldStyleSettings
              id={`${fieldId}-field`}
              {...{

                fontSize,
                onFontSizeChange: isFontSizeHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontSize: value }),

                fontColor,
                onFontColorChange: isFontColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontColor: value }),

                textAlign,
                onTextAlignChange: isTextAlignHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textAlign: value }),

                fontStyle,
                onFontStyleChange: isFontStyleHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontStyle: value }),

                fontWeight,
                onFontWeightChange: isFontWeightHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { fontWeight: value }),

                textTransform,
                onTextTransformChange: isTextTransformHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textTransform: value }),

                textDecoration,
                onTextDecorationChange: isTextDecorationHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textDecoration: value }),

                textOverflow,
                onTextOverflowChange: isTextOverflowHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { textOverflow: value }),

              }}
            />
          </AccordionSettings>
          <DividerLine />
        </>
      )}
      {(
        isBorderColorHidden
        && isBorderRadiusHidden
        && isBorderWidthHidden
        && isMarginHidden
        && isPaddingHidden
        && isBackgroundColorHidden
        && isPositionHidden
      ) ? null : (
        <AccordionSettings
          isOpen
          title="Field"
          height={330}
        >
          <FieldStyleSettings
            id={`${fieldId}-field`}
            {...{

              borderColor,
              onBorderColorChange: isBorderColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderColor: value }),

              borderRadius,
              onBorderRadiusChange: isBorderRadiusHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderRadius: value }),

              borderWidth,
              onBorderWidthChange: isBorderWidthHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { borderWidth: value }),

              margin,
              onMarginChange: isMarginHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { margin: value }),

              padding,
              onPaddingChange: isPaddingHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { padding: value }),

              backgroundColor,
              onBackgroundColorChange: isBackgroundColorHidden ? null : (value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { backgroundColor: value }),

              zIndex,
              onZIndexChange: isZIndexHidden ? null : (value) => (value ? onChange(fieldId, fieldIdentifier, fieldPositionId, { zIndex: value }) : ''),

            }}
          />
          {isPositionHidden ? null : (
            <PositionSettings
              {...{
                x,
                y,
                width,
                height,
                onChange: (field, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { [field]: value })
              }}
            />
          )}
        </AccordionSettings>
      )}
    </>
  );

  const readableIdNode = isIdHidden || !useReadableIds ? null : (
    <StyledFieldWrapper>
      <InputSettingsField
        label="ID"
        fieldId={fieldId}
        value={fieldId}
        type="text"
        rows={3}
        error={readableIdError ? true : false}
        disabled={!useReadableIds}
        onBlur={(value) => {

          if (value && !validateReadableId(value, fields) && value !== fieldId) {

            setReadableIdError(`Invalid: Can't be duplicate or start with numerics, use reserved ES6 keywords, or have spaces.`);

          } else {

            setReadableIdError('');

            if (value && value !== fieldId) {
              onChange(fieldId, fieldIdentifier, fieldPositionId, { _id: value });
            }

          }
        }}
      />
      {readableIdError && (
        <StyledErrorMessage theme={theme}>
          {readableIdError}
        </StyledErrorMessage>
      )}
    </StyledFieldWrapper>
  );

  const settingsNode = (
    <>
      {isTitleHidden ? null : (
        <>
          <SettingsLabel>Field</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
          <StyledFieldWrapper>
            <InputSettingsField
              type="text"
              fieldId={fieldPositionId}
              addonBefore={<Icon icon={ThemeIcons.fields.table} />}
              value={title}
              onChange={(i, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { title: value })}
            />
          </StyledFieldWrapper>
        </>
      )}
      <SettingsLabel>Cell</SettingsLabel>
      <DividerLine style={{ marginBottom: 14 }} />
      {!isDropdownType || isDisplayTypeHidden ? null : (
        <StyledButtonGroupWrapper>
          <ButtonGroupSettings
            id="displayType"
            types={[
              { type: FieldDisplayTypes.text, icon: ThemeIcons.settings.selectDisplayTypeOriginal, active: displayType === FieldDisplayTypes.text },
              { type: FieldDisplayTypes.check, icon: ThemeIcons.settings.selectDisplayTypeCheck, active: displayType === FieldDisplayTypes.check },
              { type: FieldDisplayTypes.radio, icon: ThemeIcons.settings.selectDisplayTypeRadio, active: displayType === FieldDisplayTypes.radio },
              { type: FieldDisplayTypes.circle, icon: ThemeIcons.settings.selectDisplayTypeCircle, active: displayType === FieldDisplayTypes.circle },
              { type: FieldDisplayTypes.square, icon: ThemeIcons.settings.selectDisplayTypeSquare, active: displayType === FieldDisplayTypes.square },
            ]}
            onChange={(value) => {
              const nextChanges = { displayType: value };

              if (value === FieldDisplayTypes.circle || value === FieldDisplayTypes.square) {
                nextChanges.borderWidth = 3;
                nextChanges.borderColor = theme.fontColorPrimary;
              }
              else {
                nextChanges.borderWidth = 1;
                nextChanges.borderColor = theme.borderColor;
              }

              onChange(fieldId, fieldIdentifier, fieldPositionId, nextChanges);
            }}
          />
        </StyledButtonGroupWrapper>
      )}
      {imageSettingsNode}
      {defaultValueNode}
      {formatNode}
      {isOptionsHidden ? null : (
        <>
          {!isSelectorType ? null : (
            <StyledFieldWrapper>
              <InputSettingsField
                type="dropdown"
                fieldId={fieldPositionId}
                addonBefore={<Icon icon={ThemeIcons.settings.targetValue} />}
                label="Target Option"
                value={targetValue}
                options={validOptions}
                onChange={(i, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { targetValue: value })}
              />
            </StyledFieldWrapper>
          )}
          {!isDropdownType ? null : (
            <>
              <StyledFieldWrapper>
                <OptionSettings
                  id="options"
                  config={columnConfig?.options}
                  activeFieldPosition={activeFieldPosition}
                  type={type}
                  targetValue={targetValue}
                  displayType={displayType}
                  options={options}
                  onChange={(optionId, data) => onOptionChange(fieldId, fieldIdentifier, fieldPositionId, optionId, data)}
                  onDelete={(optionId) => onOptionDelete(fieldId, fieldIdentifier, fieldPositionId, optionId)}
                  onCreate={(data) => onOptionCreate(fieldId, fieldIdentifier, fieldPositionId, data)}
                />
              </StyledFieldWrapper>
            </>
          )}
          {!isMultiselectType ? null : (
            <>
              <StyledFieldWrapper>
                <OptionSettings
                  id="options"
                  config={columnConfig?.options}
                  activeFieldPosition={activeFieldPosition}
                  type={columnType}
                  targetValue={targetValue}
                  displayType={displayType}
                  options={options}
                  onChange={(optionId, data) => onOptionChange(fieldId, fieldIdentifier, fieldPositionId, optionId, data)}
                  onDelete={(optionId) => onOptionDelete(fieldId, fieldIdentifier, fieldPositionId, optionId)}
                  onCreate={(data) => onOptionCreate(fieldId, fieldIdentifier, fieldPositionId, data)}
                  disableDragAndDrop={disableDragAndDrop}
                  columnType={columnType}
                />
              </StyledFieldWrapper>
            </>
          )}
        </>
      )}
      {!columnMetadataOptions ? null : (
        <>
          <DividerLine />
          <MetadataSettings
            metadata={metadata}
            metadataOptions={columnMetadataOptions}
            onChange={(changes) => {
              const nextMetadata = metadata ? { ...metadata, ...changes } : { ...changes };
              onColumnChange(fieldId, fieldIdentifier, fieldPositionId, { metadata: nextMetadata });
            }}
          />
        </>
      )}
      {readableIdNode ? (
        <>
          <DividerLine />
          {readableIdNode}
        </>
      ) : null}
      {isIdentifierHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: '0px' }} />
          <AccordionSettings
            title={config?.identifier?.label || 'Identifiers'}
            height={140}
          >
            <StyledAccordionFieldWrapper>
              <IdentifierSelectorSettings
                fieldId={fieldId}
                fieldType={type}
                label="Field"
                type="text"
                customValue={config?.identifier?.options}
                value={identifier}
                onChange={(fieldId, value) => onChange(fieldId, fieldIdentifier, fieldPositionId, { identifier: value })}
              />
            </StyledAccordionFieldWrapper>
            <StyledAccordionFieldWrapper>
              <IdentifierSelectorSettings
                fieldId={fieldId}
                fieldType={type}
                label="Column"
                type="text"
                customValue={columnConfig?.identifier?.options}
                enableCustomOption={columnConfig?.identifier?.custom}
                value={columnIdentifier}
                onChange={(fieldId, value) => onColumnChange(fieldId, fieldIdentifier, fieldPositionId, { identifier: value })}
              />
            </StyledAccordionFieldWrapper>
          </AccordionSettings>
        </>
      )}
      {isDeleteHidden ? null : (
        <>
          <DividerLine style={{ marginBottom: 14 }} />
          <StyledButtonsWrapper>
            <Button
              danger
              icon={ThemeIcons.settings.delete}
              text="Delete"
              onClick={() => onDelete(fieldId, fieldIdentifier, fieldPositionId)}
            />
            <Button
              icon={ThemeIcons.settings.clone}
              text="Duplicate"
              onClick={() => {
                onPositionDuplicate(fieldId, fieldPositionId);
              }}
              solid
            />
          </StyledButtonsWrapper>
        </>
      )}
    </>
  );

  return (
    <StyledWrapper data-testid="form-settings">
      <FieldSettingsHeaderTitle
        title={`${columnOperand}${rowIndex + 1} Settings`}
        icon={ThemeIcons.fields.tableCell}
      />
      <SettingsTabs
        settings={settingsNode}
        styles={isStylesHidden ? null : stylesNode}
      />
    </StyledWrapper>
  );

};

const getCellValueSelector = createSelector(
  (state) => state.rowIndex,
  (state) => state.column,
  (state) => state.rows,
  (rowIndex, column, rows) => {
    return rows[rowIndex]?.cells[column];
  }
);

export const getFieldLookupSelector = createSelector(
  (state) => state.fields,
  (fields) => {
    const lookup = {};
    fields.forEach((field) => lookup[field._id] = field);
    return lookup;
  }
);

export default FieldSettingsTableCell;
